// Home.tsx
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Anchor, Badge, Box, Group, Modal, Stack, Tabs, Text } from '@mantine/core';

import API, { PUBLIC_URL } from '../utils/const';
import { Account, Calendar } from '../components/AccountCard';
import LoadingOverlay from '../components/LoadingOverlay';
import { createToast } from '../components/Toast';
import { MessageKind, redirectWithParameters } from '../utils/common';
import { useTranslation } from 'react-i18next';
import AppLayout from '../layouts/AppLayout';
import { getSearchParam } from '../utils/app';
import { Head } from 'vite-react-ssg';
import { useAuth } from '../contexts/AuthContext';
import { authFetch } from '../utils/session';
import { trackCalendarConnected, trackCtaClick } from '../utils/analytics';
import { SynchronizationsTab } from '../components/SynchronizationsTab';
import { AccountTab } from '../components/AccountTab';
import { OnboardingDrawer } from '../components/onboarding/OnboardingDrawer';
import {
    isOnboardingDismissed,
    readOnboardingState,
    writeOnboardingState,
} from '../components/onboarding/state';
import { SyncRule } from '../utils/rule';
import { IconAlertCircle, IconArrowsRightLeft, IconCalendar, IconClock, IconCreditCard, IconUser, IconUsers } from '@tabler/icons-react';
import LoginCard from '../components/LoginCard';
import { ScopeErrorModal } from '../components/ScopeErrorModal';
import { ScheduleTab } from '../components/schedule/ScheduleTab';
import { FreshdeskChat } from '../components/FreshdeskChat';
import { PlanTeamViews } from '../components/PlanTeamViews';


const DASHBOARD_TAB_IDS = ['synchronizations', 'schedules', 'accounts', 'plan', 'team'] as const;

function readTabFromUrl(): string | null {
  if (typeof window === 'undefined') return null;
  const tab = new URLSearchParams(window.location.search).get('tab');
  return tab && (DASHBOARD_TAB_IDS as readonly string[]).includes(tab) ? tab : null;
}

const InnerDashboard: React.FC = () => {
  const { t } = useTranslation(['app']);

  const [accounts, setAccounts] = useState<Account[]>([]);
  const [accountsLoaded, setAccountsLoaded] = useState(false);
  const [accountsRefreshing, setAccountsRefreshing] = useState(false);
  const accountsLoadGeneration = useRef(0);
  const initialTabSetRef = useRef(false);
  const userSelectedTabRef = useRef(false);
  const [daysLeft, setDaysLeft] = useState<number>(0);
  const [activeTab, setActiveTab] = useState<string>(() => readTabFromUrl() ?? 'synchronizations');

  const { user, setRedirectOnUnauthenticated, isAuthenticated } = useAuth();

  useEffect(() => {
    setRedirectOnUnauthenticated(false);
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const params = new URLSearchParams(window.location.search);
    if (params.get('added_calendar') === 'true') {
      trackCalendarConnected('oauth');
      params.delete('added_calendar');
      const next =
        window.location.pathname +
        (params.toString() ? `?${params.toString()}` : '') +
        window.location.hash;
      window.history.replaceState({}, '', next);
    }
  }, []);

  const [onboardingOpened, setOnboardingOpened] = useState<boolean>(false);
  const [rules, setRules] = useState<SyncRule[]>([]);
  const [rulesLoaded, setRulesLoaded] = useState(false);
  const [magicValue, setMagicValue] = useState<string | null>(null);

  const queryParams = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
  const [errorParam, _] = useState(queryParams.get('error'));

  const handleMagicLink = async (_magicValue: string) => {
    window.location.href = `${API}/magic-link/${_magicValue}`
  }

  async function fetchCalendars(uuid: string): Promise<Calendar[]> {
    try {
      const response = await authFetch(
        `${API}/accounts/${uuid}/calendars`,
        {
          method: 'GET',
        }
      );
      if (!response.ok) {
        return [];
      }
      return await response.json() as Calendar[];
    } catch (error) {
      console.error(`${t("dashboard.error.fetching-calendars")}:`, error);
      return [];
    }
  }

  const loadCalendarsForAccount = useCallback(async (uuid: string, generation: number) => {
    const calendars = await fetchCalendars(uuid);
    if (generation !== accountsLoadGeneration.current) {
      return;
    }
    setAccounts((prev) =>
      prev.map((account) =>
        account.uuid === uuid ? { ...account, calendars } : account
      )
    );
  }, [t]);

  const loadAccounts = useCallback(async (options?: { preserveExisting?: boolean }) => {
    const preserveExisting = options?.preserveExisting ?? false;
    const generation = ++accountsLoadGeneration.current;

    if (!preserveExisting) {
      setAccounts([]);
      setAccountsLoaded(false);
    }

    try {
      const response = await authFetch(`${API}/accounts`, {
        method: 'GET',
      });

      if (generation !== accountsLoadGeneration.current) {
        return;
      }

      if (!response.ok) {
        if (response.status === 403) {
          setAccounts([]);
          setAccountsLoaded(true);
          return;
        }
        const error = await response.json();
        createToast(error.message || t("common.internal-server-error"), MessageKind.Error);
        throw new Error(`${t('dashboard.error.fetching-account')}: ${error.message}`);
      }

      const accountList = await response.json() as Account[];
      if (accountList.length === 0) {
        setAccounts([]);
        setAccountsLoaded(true);
        return;
      }

      const accountsWithPendingCalendars = accountList.map((account) => ({
        ...account,
        calendars: null,
      }));
      setAccounts(accountsWithPendingCalendars);

      await Promise.all(
        accountList.map((account) => loadCalendarsForAccount(account.uuid, generation))
      );

      if (generation !== accountsLoadGeneration.current) {
        return;
      }
      setAccountsLoaded(true);
    } catch {
      if (generation === accountsLoadGeneration.current && !preserveExisting) {
        setAccountsLoaded(true);
      }
    }
  }, [loadCalendarsForAccount, t]);

  const fetchSyncRules = useCallback(async () => {
    try {
      const response = await authFetch(`${API}/sync`, { method: 'GET' });
      if (response.ok) {
        setRules(await response.json());
      }
    } finally {
      setRulesLoaded(true);
    }
  }, []);

  async function updateAccounts() {
    setAccountsRefreshing(true);
    try {
      await loadAccounts({ preserveExisting: true });
    } finally {
      setAccountsRefreshing(false);
    }
  }

  useEffect(() => {
    if (errorParam === 'scope-error') {
      setActiveTab('accounts');
      initialTabSetRef.current = true;
      return;
    }

    const urlTab = readTabFromUrl();
    if (urlTab) {
      setActiveTab(urlTab);
      initialTabSetRef.current = true;
      return;
    }

    if (!accountsLoaded) {
      return;
    }

    if (initialTabSetRef.current) {
      return;
    }

    if (userSelectedTabRef.current) {
      initialTabSetRef.current = true;
      return;
    }

    initialTabSetRef.current = true;
    if (accounts.length > 0) {
      setActiveTab("synchronizations");
    } else {
      setActiveTab("accounts");
    }
  }, [accounts, errorParam, accountsLoaded]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const u = new URLSearchParams(window.location.search);
    if (activeTab === 'plan' || activeTab === 'team') {
      u.set('tab', activeTab);
    } else {
      u.delete('tab');
    }
    const qs = u.toString();
    const next = window.location.pathname + (qs ? '?' + qs : '') + window.location.hash;
    const cur = window.location.pathname + window.location.search + window.location.hash;
    if (next !== cur) {
      window.history.replaceState({}, '', next);
    }
  }, [activeTab]);

  useEffect(() => {
    const magicValue = getSearchParam('magic');
    setMagicValue(magicValue);
  }, []);

  useEffect(() => {
    if (!magicValue) { return; }
    handleMagicLink(magicValue);
  }, [magicValue])

  useEffect(() => {
    if (!user) {
      setAccounts([]);
      setAccountsLoaded(false);
      setRules([]);
      setRulesLoaded(false);
      initialTabSetRef.current = false;
      userSelectedTabRef.current = false;
      const urlTab = readTabFromUrl();
      setActiveTab(urlTab ?? 'synchronizations');
      return;
    }

    if (user.customer_id == null) {
      const currentDate = new Date();
      let targetDate = null;
      if (user.end_of_trial == null) {
        targetDate = user.date_created.getTime() + 24 * 60 * 60 * 1000 * 7;
      } else {
        targetDate = user.end_of_trial.getTime();
      }
      const timeDifference = targetDate - currentDate.getTime();
      const daysDifference = Math.ceil(timeDifference / (24 * 60 * 60 * 1000));
      setDaysLeft(daysDifference);
    }

    loadAccounts();
    fetchSyncRules();
  }, [user, loadAccounts, fetchSyncRules]);

  useEffect(() => {
    if (!accountsLoaded || !rulesLoaded || !isAuthenticated || !user) {
      return;
    }

    const saved = readOnboardingState();
    const shouldResume = saved?.active === true;
    const noAccounts = accounts.length === 0;
    const noRules = rules.length === 0;
    const dismissed = isOnboardingDismissed(user.uuid);

    if (shouldResume || (noAccounts && noRules && !dismissed)) {
      setOnboardingOpened(true);
      if (noAccounts && !saved?.active) {
        writeOnboardingState({
          active: true,
          step: 0,
          sourceCalendarUuid: null,
          destinationMode: null,
        });
      }
    }
  }, [accountsLoaded, rulesLoaded, accounts.length, rules.length, isAuthenticated, user]);

  const handleOnboardingComplete = () => {
    fetchSyncRules();
  };

  const handleOnboardingExit = () => {
    setOnboardingOpened(false);
  };

  const handleGoToSyncs = () => {
    setActiveTab('synchronizations');
    setOnboardingOpened(false);
  };

  const loginClicked = () => {
    trackCtaClick('dashboard', 'login');
    redirectWithParameters('login', { login: 'true' }, true)
  }

  const onTabChange = (value: string | null) => {
    if (!value) return;
    userSelectedTabRef.current = true;
    setActiveTab(value);
  };

  const openTab = (tab: string) => {
    userSelectedTabRef.current = true;
    setActiveTab(tab);
  };

  return (
    <>
      <FreshdeskChat hidden={onboardingOpened} />
      <div className='container col-xxl-8'>
        {isAuthenticated == null && <LoadingOverlay />}
        <ScopeErrorModal defaultOpened={errorParam === 'scope-error'} />
        {isAuthenticated != null &&
          <>
            {isAuthenticated === false &&
              <Modal
                withCloseButton={false}
                opened={true}
                onClose={() => { }}
                transitionProps={{ transition: 'fade-up', duration: 600 }}
                overlayProps={{
                  backgroundOpacity: 0.55,
                  blur: 3,
                }}
              >
                <LoginCard />
              </Modal>
            }
            {isAuthenticated == false &&
              <div className='alert alert-light py-2 mt-4 border-2'>
                {t("dashboard.already-have-account")} <Anchor onClick={loginClicked}>{t("dashboard.login")}</Anchor>
              </div>
            }
            {user != null && user.customer_id == null &&
              <Box className='container-sm p-0 my-2'>
                {daysLeft < 0 && (
                  <Group wrap="nowrap" gap="xs" align="center">
                    <IconAlertCircle size={16} style={{ flexShrink: 0 }} color="var(--mantine-color-red-6)" />
                    <Text size="sm" span c="red">
                      {t("dashboard.trial-ended")}{' '}
                      <Anchor href={`${PUBLIC_URL}/dashboard?tab=plan`} style={{ flexShrink: 0 }}>{t("dashboard.upgrade")}</Anchor>
                    </Text>
                  </Group>
                )}
                {daysLeft >= 0 && (
                  <Group wrap="nowrap" gap="xs" align="center">
                    <IconClock size={16} style={{ flexShrink: 0 }} />
                    <Text size="sm" span>
                      {t("dashboard.days-left").replace("DAYS", daysLeft.toString())}{' '}
                      <Anchor href={`${PUBLIC_URL}/dashboard?tab=plan`} style={{ flexShrink: 0 }}>{t("dashboard.upgrade")}</Anchor>
                    </Text>
                  </Group>
                )}
              </Box>
            }
            {isAuthenticated && user != null && (
              <>
                <OnboardingDrawer
                  opened={onboardingOpened}
                  onClose={handleOnboardingExit}
                  accounts={accounts}
                  user={user}
                  updateAccounts={updateAccounts}
                  onComplete={handleOnboardingComplete}
                  onGoToSyncs={handleGoToSyncs}
                />
                <Tabs onChange={onTabChange} value={activeTab} mt={{ base: 'sm', md: 'lg' }}>
                  <Tabs.List>
                    <Tabs.Tab value="synchronizations" leftSection={<IconArrowsRightLeft size={16} />}>
                      <Text hiddenFrom="xs" size="xs">{t("dashboard.tab.synchronizations")}</Text>
                      <Text visibleFrom="xs" size="md">{t("dashboard.tab.synchronizations")}</Text>
                    </Tabs.Tab>

                    <Tabs.Tab value="schedules" leftSection={<><Badge size='xs' me='4' radius='sm' variant='light'>Beta</Badge><IconCalendar size={16} /></>}>
                      <Text hiddenFrom="xs" size="xs">{t("dashboard.tab.schedules")}</Text>
                      <Text visibleFrom="xs" size="md">{t("dashboard.tab.schedules")}</Text>
                    </Tabs.Tab>
                    <Tabs.Tab value="accounts" leftSection={<IconUser size={16} />}>
                      <Text hiddenFrom="xs" size="xs">{t("dashboard.tab.accounts")}</Text>
                      <Text visibleFrom="xs" size="md">{t("dashboard.tab.accounts")}</Text>
                    </Tabs.Tab>
                    <Tabs.Tab value="plan" leftSection={<IconCreditCard size={16} />}>
                      <Text hiddenFrom="xs" size="xs">{t("dashboard.tab.plan")}</Text>
                      <Text visibleFrom="xs" size="md">{t("dashboard.tab.plan")}</Text>
                    </Tabs.Tab>
                    <Tabs.Tab value="team" leftSection={<IconUsers size={16} />}>
                      <Text hiddenFrom="xs" size="xs">{t("dashboard.tab.team")}</Text>
                      <Text visibleFrom="xs" size="md">{t("dashboard.tab.team")}</Text>
                    </Tabs.Tab>
                  </Tabs.List>
                  <Tabs.Panel value="synchronizations">
                    <SynchronizationsTab accounts={accounts} accountsLoaded={accountsLoaded} openTab={openTab} />
                  </Tabs.Panel>
                  <Tabs.Panel value="accounts">
                    <AccountTab
                      accounts={accounts}
                      accountsLoaded={accountsLoaded}
                      accountsRefreshing={accountsRefreshing}
                      updateAccounts={updateAccounts}
                    />
                  </Tabs.Panel>
                  <Tabs.Panel value="schedules">
                    <ScheduleTab accounts={accounts} accountsLoaded={accountsLoaded} openTab={openTab} />
                  </Tabs.Panel>
                  <Tabs.Panel value="plan">
                    <PlanTeamViews
                      section="billing"
                      redirectOnUnauthenticated={false}
                      showSubscriptionIssueBanner
                    />
                  </Tabs.Panel>
                  <Tabs.Panel value="team">
                    <PlanTeamViews
                      section="team"
                      redirectOnUnauthenticated={false}
                    />
                  </Tabs.Panel>
                </Tabs>
              </>
            )}
          </>
        }
      </div>
    </>
  )
};

const Dashboard: React.FC = () => {
  return <AppLayout dashboard>
    <Head>
      <title>Dashboard | Calensync</title>
      <meta property="og:title" content='Dashboard | Calensync' />
      <meta property="og:url" content={`https://calensync.live${PUBLIC_URL}`} />
      <meta property="og:description" content='Create your synchronization rules between your Google, Outlook, and Apple calendars' />
    </Head>
    <InnerDashboard />
  </AppLayout>
}

export default Dashboard;
